/* NOTE: When animating between the closed and open states we transition both the WIDTH of the :host and the POSITION of the inner section. Why do we do both, when it just seems easier to transition the left/right value of the :host itself? If we didn't then we would need to rely on the <aha-sidebar>'s host element to have specific CSS for the animation effects to work properly, which makes the <aha-sidebar> less generic than we would like. By animating both properties, we make it possible for <aha-sidebar> to work almost anywhere, without any specific parent CSS. */

:host {
  --aha-sidebar--padding: var(--spacer-3);
  --aha-sidebar--background-color: var(--theme-primary-background);
  --aha-sidebar--animation-duration: 0.3s;
  --aha-sidebar--animation-timing-function: linear;
  --aha-sidebar--animation-fill-mode: forwards;

  display: block;
  min-height: 100%;
  position: relative;
  background-color: var(--aha-sidebar--background-color);
  width: var(--aha-sidebar--collapsed-width, 0);
}
:host,
:host * {
  box-sizing: border-box;
}
:host([paused]) {
  --aha-sidebar--animation-duration: 0s;
}

:host #wrapper {
  background-color: inherit;
  border: inherit;
  overflow: hidden;
  position: absolute;
  top: 0;
  bottom: 0;
  width: inherit;
  animation-timing-function: var(--aha-sidebar--animation-timing-function);
  animation-duration: var(--aha-sidebar--animation-duration);
  animation-fill-mode: var(--aha-sidebar--animation-fill-mode);
  animation-name: var(--aha-sidebar--animation-name);
}
:host([expands="right"]) #wrapper {
  left: 0;
}
:host([expands="left"]) #wrapper {
  right: 0;
}

section {
  width: var(--aha-sidebar--width);
  height: 100%;
  display: flex;
  background-color: inherit;
  position: relative;
  flex-direction: row;
  align-items: stretch;
  overflow: hidden;
  gap: 2px;
}
:host([open]) section {
  padding-bottom: var(--aha-sidebar--padding);
  flex-direction: column;
  overflow: auto;
}
header {
  min-height: min-content;
  display: flex;
  gap: var(--aha-sidebar--padding);
  background-color: inherit;
  position: sticky;
  top: 0;
  left: 0;
  z-index: 1;
  padding-left: var(--aha-sidebar--padding);
  padding-right: var(--aha-sidebar--padding);
}
:host(:not([open])) header {
  padding: var(--aha-sidebar--padding);
  writing-mode: vertical-rl;
  text-orientation: mixed;
}
:host([open]) header {
  padding-top: var(--aha-sidebar--padding);
  padding-bottom: var(--aha-sidebar--padding);

  &.with-border {
   border-bottom: 1px solid var(--aha-gray-600);
  }
}

/* Draw a small semi-transparent area underneath the header, which gradually fades the text in the body of the panel as it slides underneath the header. Makes for a more natural feeling scroll effect. */
:host([open]) header::before {
  content: "";
  position: absolute;
  top: 100%;
  left: 0;
  height: 3px;
  width: 100%;
  z-index: 1;
  background: linear-gradient(to bottom, color-mix(in oklch, var(--aha-sidebar--background-color), transparent 20%), transparent);
}

#label, #close {
  font-size: 1rem;
}

#close {
  background: none;
  border: none;
  display: inline-flex;
  place-items: center;
  color: var(--theme-tertiary-text);
  padding: 0;
  cursor: pointer;
}
#close:where(:hover) {
  color: var(--theme-link-text);
}
/* Make the expand button have a large surface-area when the aha-sidebar is closed. This makes for a larger click target. */
#close::before {
  content: '';
  position: absolute;
  inset: 0;
}

/* Make the collapse button have a slightly larger surface-area when the aha-sidebar is open. This makes for a larger click target. */
:host([open]) #close {
  position: relative;
}
:host([open]) #close::before {
  inset: calc(-1 * (var(--aha-sidebar--padding) / 2));
}

#header-placeholder {
  height: var(--aha-sidebar--collapsed-width);
  width: 100%;
  margin-bottom: var(--aha-sidebar--padding);
}
:host([open]) #header-placeholder {
  display: none;
}
#content {
  flex: 1;
  padding-left: var(--aha-sidebar--padding);
  padding-right: var(--aha-sidebar--padding);
}

:host([open][expands="right"]) header {
  flex-direction: row-reverse;
}
:host([open][expands="right"]) #label {
  margin-right: auto;
}
:host([open][hide-opened-label]) #label {
  display: none;
}
:host(:not([open]) + [expands="right"]) #label {
  transform-origin: center;
  rotate: 180deg;
}
:host(:not([open])) #label {
  color: var(--theme-tertiary-text);
}
:host([open]) #close {
  transform-origin: center;
  rotate: 180deg;
}

/* Styles for the non-`float` option */
:host(:not([float])) {
  /* When NOT floated, we also want the <aha-sidebar> itself to grow and take up horizontal space, so we add the same animation to the host itself. */
  animation-timing-function: var(--aha-sidebar--animation-timing-function);
  animation-duration: var(--aha-sidebar--animation-duration);
  animation-fill-mode: var(--aha-sidebar--animation-fill-mode);
  animation-name: var(--aha-sidebar--animation-name);
}
:host([open]) {
  --aha-sidebar--animation-name: slide-in;
}
:host(:not([open])) {
  --aha-sidebar--animation-name: slide-out;
}

/* Ensure that the sidebar is the specified width when a page is rendered and it is already in an "open" state, but before the width has been calculated. This will automatically set the correct width when the `width` attribute has been set. Fixes an issue found in Capybara tests where an open sidebar has a zero width until the `width` attribute has been set. */
:host([open][width]) {
  width: var(--aha-sidebar--width);
}


/* Styles for the `float` option */
:host([float]) {
  z-index: 99;
}
:host([float][open]) {
  --aha-sidebar--animation-name: float-slide-in;
}
:host([float]:not([open])) {
  --aha-sidebar--animation-name: float-slide-out;
}

/* Effectively --theme-shadow-medium but only on the left side */
:host([float][expands="left"]) #wrapper {
  box-shadow: -6px 0 6px -6px rgba(0, 0, 0, 0.2);
}
/* Effectively --theme-shadow-medium but only on the right side */
:host([float][expands="right"]) #wrapper {
  box-shadow: 6px 0 6px -6px rgba(0, 0, 0, 0.2);
}


@keyframes slide-in {
  0% {
    width: var(--aha-sidebar--collapsed-width, 0);
  }
  100% {
    width: var(--aha-sidebar--width);
  }
}

@keyframes slide-out {
  0% {
    width: var(--aha-sidebar--width);
  }
  100% {
    width: var(--aha-sidebar--collapsed-width, 0);
  }
}

@keyframes float-slide-in {
  0% {
    width: var(--aha-sidebar--collapsed-width, 0);
  }
  50%, 60% {
    width: calc(var(--spacer-8) + var(--aha-sidebar--width));
  }
  100% {
    width: var(--aha-sidebar--width);
  }
}

@keyframes float-slide-out {
  0% {
    width: var(--aha-sidebar--width);
  }
  25%, 35% {
    width: calc(var(--spacer-4) + var(--aha-sidebar--width));
  }
  100% {
    width: var(--aha-sidebar--collapsed-width, 0);
  }
}
